const sectionSelector = '.fill-complete-moment';
const productFormSelector = '.fill-complete-moment__product-form';
const formSelector = 'form[data-type="add-to-cart-form"]';
const buttonSelector = '.fill-complete-moment__button--add';
const errorSelector = '[data-fill-complete-moment-error]';
const pendingAttribute = 'data-fill-complete-moment-pending';
const defaultErrorMessage = "We couldn't add this moment. Please try again.";
const unavailableMessage = 'This moment is currently unavailable.';
const optionsMessage = 'Please choose the required options first.';

function getSection(element) {
  return element?.closest?.(sectionSelector);
}

function getProductForm(form) {
  return form?.closest?.(productFormSelector);
}

function getPanel(element) {
  return element?.closest?.('.fill-complete-moment__panel');
}

function getButton(productForm) {
  return productForm?.querySelector?.(buttonSelector);
}

function getErrorElement(productForm) {
  return getPanel(productForm)?.querySelector?.(errorSelector);
}

function setError(productForm, message) {
  const errorElement = getErrorElement(productForm);
  if (!errorElement) return;

  errorElement.textContent = message || defaultErrorMessage;
  errorElement.hidden = false;
}

function clearError(productForm) {
  const errorElement = getErrorElement(productForm);
  if (!errorElement) return;

  errorElement.textContent = '';
  errorElement.hidden = true;
}

function setButtonState(productForm, isLoading) {
  const button = getButton(productForm);
  if (!button) return;

  if (!button.dataset.originalAriaLabel && button.getAttribute('aria-label')) {
    button.dataset.originalAriaLabel = button.getAttribute('aria-label');
  }

  button.toggleAttribute('data-loading', isLoading);
  button.toggleAttribute('disabled', isLoading);
  button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  button.removeAttribute('data-added');

  if (isLoading) {
    button.setAttribute('aria-label', 'Adding...');
  } else if (button.dataset.originalAriaLabel) {
    button.setAttribute('aria-label', button.dataset.originalAriaLabel);
  }
}

function setFormPending(productForm, isPending) {
  productForm.toggleAttribute(pendingAttribute, isPending);
  setButtonState(productForm, isPending);
}

function getErrorMessage(error) {
  const data = error?.data || error;

  if (typeof data?.description === 'string' && data.description.trim()) {
    return data.description;
  }

  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error;
  }

  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).flat().find(Boolean);
    if (typeof firstError === 'string' && firstError.trim()) return firstError;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return defaultErrorMessage;
}

async function parseCartResponse(response) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    if (!response.ok) throw new Error(defaultErrorMessage);
    return {};
  }
}

async function addBundleToCart(form) {
  const variantInput = form.querySelector('input[name="id"]');
  if (!variantInput?.value) {
    throw new Error(unavailableMessage);
  }

  const cartAddUrl = form.getAttribute('action') || window.Theme?.routes?.cart_add_url;
  if (!cartAddUrl) {
    throw new Error(defaultErrorMessage);
  }

  const formData = new FormData(form);
  formData.set('id', variantInput.value);
  formData.set('quantity', '1');

  const response = await fetch(cartAddUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: formData,
    credentials: 'same-origin',
  });
  const data = await parseCartResponse(response);

  if (!response.ok || data?.status) {
    const error = new Error(getErrorMessage(data));
    error.data = data;
    throw error;
  }

  return data;
}

function redirectToCart(section) {
  const cartUrl = section?.dataset.cartUrl || window.Theme?.routes?.cart_url;
  if (!cartUrl) {
    throw new Error(defaultErrorMessage);
  }

  window.location.assign(cartUrl);
}

async function handleSubmit(event) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const form = event.currentTarget;
  const productForm = getProductForm(form);
  const section = getSection(form);
  if (!productForm || !section) return;

  if (productForm.hasAttribute(pendingAttribute)) return;

  if (!form.checkValidity()) {
    setError(productForm, optionsMessage);
    return;
  }

  setFormPending(productForm, true);
  clearError(productForm);

  try {
    await addBundleToCart(form);
    redirectToCart(section);
  } catch (error) {
    setError(productForm, getErrorMessage(error));
    setFormPending(productForm, false);
  }
}

function initSection(section) {
  if (section.dataset.fillCompleteMomentReady === 'true') return;
  section.dataset.fillCompleteMomentReady = 'true';

  section.querySelectorAll(formSelector).forEach((form) => {
    if (getProductForm(form)) {
      form.addEventListener('submit', handleSubmit, true);
    }
  });
}

function initAllSections(root = document) {
  root.querySelectorAll(sectionSelector).forEach(initSection);
}

document.addEventListener('shopify:section:load', (event) => {
  initAllSections(event.target);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllSections(), { once: true });
} else {
  initAllSections();
}
